import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { Toaster } from '@easyflow/ui-master/components/ui/toaster';
import { StorageQuotaErrorToastProvider } from '@easyflow/ui-frontend';
import { appPath, stripBasePath } from '@/lib/basePath';
import { getCurrentAdminSurfaceContext, isCurrentDedicatedCrmHost } from '@/lib/paths';
import { buildCrmUrl } from '@easyflow/shared';
import ErrorBoundary from '@/components/ErrorBoundary';
import MasterLayout from '@/components/layout/MasterLayout';
import EasyCrmLayout from '@/components/layout/EasyCrmLayout';
import ImpersonationBanner from '@/components/ImpersonationBanner';
import MasterLogin from '@/pages/MasterLogin';
import MasterResetPassword from '@/pages/MasterResetPassword';
import DashboardMaster from '@/pages/DashboardMaster';
import TenantsMaster from '@/pages/TenantsMaster';
import StoresMaster from '@/pages/StoresMaster';
import CompaniesMaster from '@/pages/CompaniesMaster';
import BillingMaster from '@/pages/BillingMaster';
import TemplatesMaster from '@/pages/TemplatesMaster';
import RateLimitsMaster from '@/pages/RateLimitsMaster';
import TenantUsersMaster from '@/pages/TenantUsersMaster';
import MasterUsersMaster from '@/pages/MasterUsersMaster';
import AnalyticsMaster from '@/pages/AnalyticsMaster';

// Task #1943 — Security Center: code-split / lazy load (5 pagine + sotto-pagine Fail2Ban)
const SecurityLayout = React.lazy(() => import('@/components/security/_layout/SecurityLayout'));
const SecurityOverviewPage = React.lazy(() => import('@/pages/security/Overview'));
const SecuritySbcPage = React.lazy(() => import('@/pages/security/Sbc'));
const SecurityTrunkTrustPage = React.lazy(() => import('@/pages/security/TrunkTrust'));
const SecurityAclListPage = React.lazy(() => import('@/pages/security/AclList'));
const SecurityGeoblockPage = React.lazy(() => import('@/pages/security/Geoblock'));
const SecurityFirewallPage = React.lazy(() => import('@/pages/security/Firewall'));
const SecuritySessionsPage = React.lazy(() => import('@/pages/security/Sessions'));
const Fail2BanLayout = React.lazy(() => import('@/components/security/_layout/Fail2BanLayout'));
const Fail2BanPanoramica = React.lazy(() => import('@/pages/security/fail2ban/Panoramica'));
const Fail2BanBannati = React.lazy(() => import('@/pages/security/fail2ban/Bannati'));
const Fail2BanRecidivi = React.lazy(() => import('@/pages/security/fail2ban/Recidivi'));
const Fail2BanWhitelist = React.lazy(() => import('@/pages/security/fail2ban/Whitelist'));
const Fail2BanStorico = React.lazy(() => import('@/pages/security/fail2ban/Storico'));
const Fail2BanDiagnostica = React.lazy(() => import('@/pages/security/fail2ban/Diagnostica'));
const Fail2BanJails = React.lazy(() => import('@/pages/security/fail2ban/Jails'));

import AuditLog from '@/pages/AuditLog';
import CreateTenant from '@/pages/CreateTenant';
import TenantDetail from '@/pages/TenantDetail';
import SipClusterMonitor from '@/pages/SipClusterMonitor';
import TrunksMaster from '@/pages/TrunksMaster';
import ExtensionsMaster from '@/pages/ExtensionsMaster';
import NetworkMonitoring from '@/pages/NetworkMonitoring';
import SystemMonitoring from '@/pages/SystemMonitoring';
import PublicContextEditor from '@/pages/PublicContextEditor';
import SpamDashboard from '@/pages/SpamDashboard';
import QueueMonitorPage from '@/pages/QueueMonitorPage';
import InfraMap from '@/pages/InfraMap';
import DatabaseManager from '@/pages/DatabaseManager';
import TicketManagement from '@/pages/TicketManagement';
import CRMMaster from '@/pages/CRMMaster';
import ContractSign from '@/pages/ContractSign';
import PaymentSetup, { PaymentSetupSuccess, PaymentSetupCancel } from '@/pages/PaymentSetup';
import StorageMaster from '@/pages/StorageMaster';
import StorageTenantDetail from '@/pages/StorageTenantDetail';
import AIQuotasMaster from '@/pages/AIQuotasMaster';
import AIModelsUnified from '@/pages/AIModelsUnified';
import IntegrationsMaster from '@/pages/IntegrationsMaster';
import RetailBrandChannelsMaster from '@/pages/RetailBrandChannelsMaster';
import RAGQualityMaster from '@/pages/RAGQualityMaster';
import TestVerifyPage from '@/pages/TestVerifyPage';
import MasterSecuritySettings from '@/pages/MasterSecuritySettings';
import ObservabilityDashboards from '@/pages/ObservabilityDashboards';
import CloudBeaverStudio from '@/pages/CloudBeaverStudio';

function ProtectedRoute({ children, loginPath = '/login' }: { children: React.ReactNode; loginPath?: string }) {
  const { isAuthenticated, isLoading, token } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  if (!isAuthenticated || !token) {
    return <Navigate to={loginPath} replace />;
  }

  return <>{children}</>;
}

// Task #3055 — Guard della console operativa Master. CRM (EasyCRM) è fuori dalla
// console operativa: chi non è super_admin (es. ruolo "admin_crm") viene
// reindirizzato dentro EasyCRM e non può accedere alle pagine operative.
function OperationalConsoleGuard({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  if (user?.role !== 'super_admin') {
    return <CrossOriginCrmRedirect />;
  }

  return <>{children}</>;
}

function CrossOriginCrmRedirect(): null {
  React.useEffect(() => {
    window.location.replace(buildCrmUrl('/'));
  }, []);
  return null;
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000,
    },
  },
});

function SecurityLazyFallback(): JSX.Element {
  return (
    <div className="min-h-[320px] flex items-center justify-center">
      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600" />
    </div>
  );
}

function App() {
  const surfaceContext = getCurrentAdminSurfaceContext();
  const isCrmSurface = isCurrentDedicatedCrmHost();

  React.useEffect(() => {
    // Ogni host amministra una sola superficie. L'host CRM non può rendere la
    // console Master per errore, anche se condivide lo stesso dist statico.
    const stripped = stripBasePath(window.location.pathname);
    const isProtectedRoute = stripped !== '/login'
      && stripped !== '/reset-password'
      && !stripped.startsWith('/sign/')
      && !stripped.startsWith('/payment-setup');
    const hasToken = localStorage.getItem('edgvoip_master_token');

    if (isProtectedRoute && !hasToken) {
      console.warn('🔒 No token found, redirecting to login...');
      window.location.href = appPath(surfaceContext.loginPath);
    }
  }, [surfaceContext.loginPath]);

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
      <Router basename={import.meta.env.BASE_URL.replace(/\/$/, '')} future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <AuthProvider>
          <div className="min-h-screen bg-background">
            <ImpersonationBanner />
            <StorageQuotaErrorToastProvider
              userRole="master"
              onNavigateDetail={() => { window.location.href = appPath('/storage'); }}
            />
            <ErrorBoundary>
              <Routes>
                <Route path="/login" element={<MasterLogin variant={isCrmSurface ? 'crm' : 'master'} />} />
                <Route path="/reset-password" element={<MasterResetPassword />} />
                {isCrmSurface ? (
                  <>
                    <Route path="/" element={<ProtectedRoute loginPath={surfaceContext.loginPath}><EasyCrmLayout /></ProtectedRoute>}>
                      <Route index element={<Navigate to={surfaceContext.homePath === '/' ? '/azienda' : `${surfaceContext.homePath}/azienda`} replace />} />
                      <Route path=":section" element={<ErrorBoundary><CRMMaster /></ErrorBoundary>} />
                    </Route>
                    <Route path="/crm/*" element={<Navigate to="/" replace />} />
                    <Route path="*" element={<Navigate to="/login" replace />} />
                  </>
                ) : (
                  <>
                    <Route path="/crm/login" element={<MasterLogin variant="crm" />} />
                    <Route path="/sign/:token" element={<ContractSign />} />
                    <Route path="/payment-setup/success" element={<PaymentSetupSuccess />} />
                    <Route path="/payment-setup/cancel" element={<PaymentSetupCancel />} />
                    <Route path="/payment-setup/:token" element={<PaymentSetup />} />
                    <Route path="/crm" element={<ProtectedRoute loginPath="/crm/login"><EasyCrmLayout /></ProtectedRoute>}>
                      <Route index element={<Navigate to="/crm/azienda" replace />} />
                      <Route path=":section" element={<ErrorBoundary><CRMMaster /></ErrorBoundary>} />
                    </Route>
                    <Route path="/" element={<ProtectedRoute><OperationalConsoleGuard><MasterLayout /></OperationalConsoleGuard></ProtectedRoute>}>
                  <Route index element={<Navigate to="/dashboard" replace />} />
                  <Route path="dashboard" element={<ErrorBoundary><DashboardMaster /></ErrorBoundary>} />
                  <Route path="tenants" element={<ErrorBoundary><TenantsMaster /></ErrorBoundary>} />
                  <Route path="tenants/create" element={<ErrorBoundary><CreateTenant /></ErrorBoundary>} />
                  <Route path="tenants/:tenantId" element={<ErrorBoundary><TenantDetail /></ErrorBoundary>} />
                  <Route path="stores" element={<ErrorBoundary><StoresMaster /></ErrorBoundary>} />
                  <Route path="companies" element={<ErrorBoundary><CompaniesMaster /></ErrorBoundary>} />
                  <Route path="sip-cluster" element={<ErrorBoundary><SipClusterMonitor /></ErrorBoundary>} />
                  <Route path="billing" element={<ErrorBoundary><BillingMaster /></ErrorBoundary>} />
                  <Route path="templates" element={<ErrorBoundary><TemplatesMaster /></ErrorBoundary>} />
                  <Route path="rate-limits" element={<ErrorBoundary><RateLimitsMaster /></ErrorBoundary>} />
                  <Route path="users" element={<Navigate to="/users-tenant?legacy=1" replace />} />
                  <Route path="users-tenant" element={<ErrorBoundary><TenantUsersMaster /></ErrorBoundary>} />
                  <Route path="users-master" element={<ErrorBoundary><MasterUsersMaster /></ErrorBoundary>} />
                  <Route path="analytics" element={<ErrorBoundary><AnalyticsMaster /></ErrorBoundary>} />
                  <Route
                    path="security"
                    element={
                      <ErrorBoundary>
                        <Suspense fallback={<SecurityLazyFallback />}>
                          <SecurityLayout />
                        </Suspense>
                      </ErrorBoundary>
                    }
                  >
                    <Route index element={<Suspense fallback={<SecurityLazyFallback />}><SecurityOverviewPage /></Suspense>} />
                    <Route
                      path="fail2ban"
                      element={<Suspense fallback={<SecurityLazyFallback />}><Fail2BanLayout /></Suspense>}
                    >
                      <Route index element={<Suspense fallback={<SecurityLazyFallback />}><Fail2BanPanoramica /></Suspense>} />
                      <Route path="jails" element={<Suspense fallback={<SecurityLazyFallback />}><Fail2BanJails /></Suspense>} />
                      <Route path="bannati" element={<Suspense fallback={<SecurityLazyFallback />}><Fail2BanBannati /></Suspense>} />
                      <Route path="recidivi" element={<Suspense fallback={<SecurityLazyFallback />}><Fail2BanRecidivi /></Suspense>} />
                      <Route path="whitelist" element={<Suspense fallback={<SecurityLazyFallback />}><Fail2BanWhitelist /></Suspense>} />
                      <Route path="storico" element={<Suspense fallback={<SecurityLazyFallback />}><Fail2BanStorico /></Suspense>} />
                      <Route path="diagnostica" element={<Suspense fallback={<SecurityLazyFallback />}><Fail2BanDiagnostica /></Suspense>} />
                      {/* Superpoteri tab eliminato: azioni cluster integrate come bottone "Azioni cluster" su "IP bannati". */}
                      <Route path="superpoteri" element={<Navigate to="/security/fail2ban/bannati?action=cluster" replace />} />
                    </Route>
                    <Route path="sbc" element={<Suspense fallback={<SecurityLazyFallback />}><SecuritySbcPage /></Suspense>} />
                    <Route path="trunk-trust" element={<Suspense fallback={<SecurityLazyFallback />}><SecurityTrunkTrustPage /></Suspense>} />
                    <Route path="acl-list" element={<Suspense fallback={<SecurityLazyFallback />}><SecurityAclListPage /></Suspense>} />
                    <Route path="geoblock" element={<Suspense fallback={<SecurityLazyFallback />}><SecurityGeoblockPage /></Suspense>} />
                    <Route path="firewall" element={<Suspense fallback={<SecurityLazyFallback />}><SecurityFirewallPage /></Suspense>} />
                    <Route path="sessions" element={<Suspense fallback={<SecurityLazyFallback />}><SecuritySessionsPage /></Suspense>} />
                  </Route>
                  <Route path="audit-log" element={<ErrorBoundary><AuditLog /></ErrorBoundary>} />
                  <Route path="database" element={<ErrorBoundary><DatabaseManager /></ErrorBoundary>} />
                  <Route path="tickets" element={<ErrorBoundary><TicketManagement /></ErrorBoundary>} />
                  <Route path="storage" element={<ErrorBoundary><StorageMaster /></ErrorBoundary>} />
                  <Route path="storage/tenant/:tenantId" element={<ErrorBoundary><StorageTenantDetail /></ErrorBoundary>} />
                  <Route path="ai-quotas" element={<ErrorBoundary><AIQuotasMaster /></ErrorBoundary>} />
                  <Route path="ai-models" element={<ErrorBoundary><AIModelsUnified /></ErrorBoundary>} />
                  <Route path="integrations" element={<ErrorBoundary><IntegrationsMaster /></ErrorBoundary>} />
                  <Route path="retail-brand-channels" element={<ErrorBoundary><RetailBrandChannelsMaster /></ErrorBoundary>} />
                  <Route path="rag-quality" element={<ErrorBoundary><RAGQualityMaster /></ErrorBoundary>} />
                  <Route path="test-verify" element={<ErrorBoundary><TestVerifyPage /></ErrorBoundary>} />
                  <Route path="trunks" element={<ErrorBoundary><TrunksMaster /></ErrorBoundary>} />
                  <Route path="extensions" element={<ErrorBoundary><ExtensionsMaster /></ErrorBoundary>} />
                  <Route path="network-monitoring" element={<ErrorBoundary><NetworkMonitoring /></ErrorBoundary>} />
                  <Route path="system" element={<ErrorBoundary><SystemMonitoring /></ErrorBoundary>} />
                  <Route path="public-context" element={<ErrorBoundary><PublicContextEditor /></ErrorBoundary>} />
                  <Route path="spam" element={<ErrorBoundary><SpamDashboard /></ErrorBoundary>} />
                  <Route path="queues" element={<ErrorBoundary><QueueMonitorPage /></ErrorBoundary>} />
                  <Route path="infra-map" element={<ErrorBoundary><InfraMap /></ErrorBoundary>} />
                  <Route path="account/security" element={<ErrorBoundary><MasterSecuritySettings /></ErrorBoundary>} />
                  <Route path="observability" element={<ErrorBoundary><ObservabilityDashboards /></ErrorBoundary>} />
                  <Route path="db-studio" element={<ErrorBoundary><CloudBeaverStudio /></ErrorBoundary>} />
                    </Route>
                    <Route path="*" element={<Navigate to="/login" replace />} />
                  </>
                )}
              </Routes>
            </ErrorBoundary>
            <Toaster />
          </div>
        </AuthProvider>
      </Router>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
